import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings2 } from 'lucide-react';
import type { JournalTemplateRow } from '@/hooks/useJournalTemplates';
import { filterJournalTemplates, partitionJournalTemplates } from '@/utils/journalTemplates';
import { Button } from '@/components/ui/Button';
import { FieldLabel } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

interface JournalTemplatePickerProps {
  templates: JournalTemplateRow[];
  value: string;
  onChange: (templateId: string) => void;
  onManage?: () => void;
  disabled?: boolean;
}

export function JournalTemplatePicker({
  templates,
  value,
  onChange,
  onManage,
  disabled,
}: JournalTemplatePickerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const showSearch = templates.length >= 8;
  const filtered = useMemo(() => filterJournalTemplates(templates, query), [templates, query]);
  const { personal, clinic } = partitionJournalTemplates(filtered);

  return (
    <div>
      <div className="flex flex-nowrap items-center justify-between gap-2">
        <FieldLabel htmlFor="journal-template-picker">{t('journal.template')}</FieldLabel>
        {onManage && (
          <Button type="button" variant="link" className="shrink-0" onClick={onManage}>
            <Settings2 />
            {t('journal.manage_templates')}
          </Button>
        )}
      </div>
      <div className="mt-s1 space-y-2">
        {showSearch && (
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('journal.template_search_placeholder')}
          />
        )}
        <Select
          id="journal-template-picker"
          data-testid="journal-template-picker"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">{t('journal.blank_journal')}</option>
          {personal.length > 0 && (
            <optgroup label={t('journal.my_templates')}>
              {personal.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </optgroup>
          )}
          {clinic.length > 0 && (
            <optgroup label={t('journal.clinic_templates')}>
              {clinic.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </optgroup>
          )}
        </Select>
      </div>
    </div>
  );
}
